import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  createTelegramLinkCode,
  getSheetsSettings,
  getSlaSettings,
  getTelegramChats,
  getTelegramMode,
  getTelegramSettings,
  patchSheetsSettings,
  patchSlaSettings,
  patchTelegramSettings,
  sendTelegramTestMessage,
  testSheetsAccess,
  testTelegramConnection,
  unbindTelegramChat,
  writeSheetsTestRow,
} from '../api/settings';
import type { SheetsSettings, SlaSettings, TelegramSettings } from '../api/types';

export function useSlaSettings() {
  return useQuery({ queryKey: ['settings', 'sla'], queryFn: getSlaSettings });
}
export function usePatchSlaSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<SlaSettings>) => patchSlaSettings(body),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['settings', 'sla'] }),
  });
}

export function useTelegramSettings() {
  return useQuery({ queryKey: ['settings', 'telegram'], queryFn: getTelegramSettings });
}
export function usePatchTelegramSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<TelegramSettings> & { bot_token?: string | null }) => patchTelegramSettings(body),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['settings', 'telegram'] }),
  });
}

/** `TG_MODE=mock` banner + whether this workspace's own token is what's actually sending. */
export function useTelegramMode() {
  return useQuery({ queryKey: ['settings', 'telegram', 'mode'], queryFn: getTelegramMode });
}

export function useTelegramChats() {
  return useQuery({ queryKey: ['settings', 'telegram', 'chats'], queryFn: getTelegramChats });
}
export function useUnbindTelegramChat() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (kind: 'leads_chat' | 'alerts_chat' | 'team_chat') => unbindTelegramChat(kind),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['settings', 'telegram', 'chats'] }),
  });
}

/** "Проверить связь": calls getMe, then refreshes both the settings section and the mode banner. */
export function useTestTelegramConnection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: testTelegramConnection,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['settings', 'telegram'] });
    },
  });
}

export function useSendTelegramTestMessage() {
  return useMutation({
    mutationFn: (body: { chat_role?: 'leads' | 'alerts' | 'team'; chat_id?: number; text?: string }) =>
      sendTelegramTestMessage(body),
  });
}

export function useCreateTelegramLinkCode() {
  return useMutation({
    mutationFn: (kind: 'leads_chat' | 'alerts_chat' | 'team_chat') => createTelegramLinkCode(kind),
  });
}

export function useSheetsSettings() {
  return useQuery({ queryKey: ['settings', 'sheets'], queryFn: getSheetsSettings });
}
export function usePatchSheetsSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<SheetsSettings> & { spreadsheet_url_or_id?: string | null; service_account_json?: string | null }) =>
      patchSheetsSettings(body),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['settings', 'sheets'] }),
  });
}

export function useTestSheetsAccess() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: testSheetsAccess,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['settings', 'sheets'] }),
  });
}

export function useWriteSheetsTestRow() {
  return useMutation({ mutationFn: writeSheetsTestRow });
}
